import { language, t, translatedRecord } from './i18n';

/*
 * Dates in the reader's language: Moroccan French, Moroccan Arabic (which keeps
 * Western digits, as Morocco does), or British English for day-month order.
 */
const DATE_LOCALE = language === 'ar' ? 'ar-MA' : language === 'en' ? 'en-GB' : 'fr-MA';
/**
 * Display helpers.
 *
 * Money arrives as integer centimes and is never divided before it is rendered.
 * Dates arrive as "YYYY-MM-DD" calendar days or ISO instants, and both are shown
 * in Casablanca time, which is the timezone every user of this product is in.
 */

export const TIMEZONE = 'Africa/Casablanca';

/** "2 400 DH" — grouped with a plain space, not the runtime's locale guess. */
export function mad(centimes: number | null | undefined): string {
  if (centimes === null || centimes === undefined) return '—';
  const dirhams = Math.round(centimes / 100);
  const grouped = String(Math.abs(dirhams)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${dirhams < 0 ? '-' : ''}${grouped} ${language === 'ar' ? 'درهم' : 'DH'}`;
}

export const perUnit = (unit: 'night' | 'month'): string => (unit === 'night' ? t('/nuit') : t('/mois'));

const DAY_FORMATTER = new Intl.DateTimeFormat(DATE_LOCALE, {
  timeZone: TIMEZONE,
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const DAY_SHORT_FORMATTER = new Intl.DateTimeFormat(DATE_LOCALE, {
  timeZone: TIMEZONE,
  day: 'numeric',
  month: 'short',
});

const MONTH_FORMATTER = new Intl.DateTimeFormat(DATE_LOCALE, {
  timeZone: TIMEZONE,
  month: 'long',
  year: 'numeric',
});

const TIME_FORMATTER = new Intl.DateTimeFormat(DATE_LOCALE, {
  timeZone: TIMEZONE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/**
 * A calendar day is rendered from its own parts. Parsing "2026-10-02" as an
 * instant and formatting it in a timezone is how a move-in date becomes the 1st
 * of October for someone west of UTC.
 */
export function day(calendarDay: string | null | undefined, short = false): string {
  if (!calendarDay) return '—';
  const [year, month, date] = calendarDay.split('-').map(Number);
  if (!year || !month || !date) return calendarDay;
  const asUtcNoon = new Date(Date.UTC(year, month - 1, date, 12));
  return (short ? DAY_SHORT_FORMATTER : DAY_FORMATTER).format(asUtcNoon);
}

export function monthOf(calendarDay: string): string {
  const [year, month] = calendarDay.split('-').map(Number);
  return MONTH_FORMATTER.format(new Date(Date.UTC(year ?? 2026, (month ?? 1) - 1, 15, 12)));
}

export const instant = (iso: string | null | undefined): string =>
  iso ? `${DAY_FORMATTER.format(new Date(iso))} · ${TIME_FORMATTER.format(new Date(iso))}` : '—';

export const clockTime = (iso: string): string => TIME_FORMATTER.format(new Date(iso));

/** "il y a 3 h" — for message lists and notification feeds. */
export function relative(iso: string): string {
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return t("à l'instant");
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return t('il y a {count} min', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('il y a {count} h', { count: hours });
  const days = Math.round(hours / 24);
  if (days < 7) return t('il y a {count} j', { count: days });
  return day(new Date(iso).toISOString().slice(0, 10), true);
}

export const today = (): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

export function addDays(calendarDay: string, days: number): string {
  const [year, month, date] = calendarDay.split('-').map(Number);
  const shifted = new Date(Date.UTC(year ?? 2026, (month ?? 1) - 1, (date ?? 1) + days));
  return shifted.toISOString().slice(0, 10);
}

export function addMonths(calendarDay: string, months: number): string {
  const [year, month, date] = calendarDay.split('-').map(Number);
  const target = new Date(Date.UTC(year ?? 2026, (month ?? 1) - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(date ?? 1, lastDay));
  return target.toISOString().slice(0, 10);
}

export function nightsBetween(start: string, end: string): number {
  const toMs = (value: string) => {
    const [year, month, date] = value.split('-').map(Number);
    return Date.UTC(year ?? 2026, (month ?? 1) - 1, date ?? 1);
  };
  return Math.round((toMs(end) - toMs(start)) / 86_400_000);
}

// ------------------------------------------------------------------ labels ---

export const MODE_LABEL: Record<string, string> = translatedRecord({
  COLOCATION: 'Colocation',
  MONTHLY: 'Chambre au mois',
  NIGHTLY: 'Nuitée',
});

export const MODE_SHORT: Record<string, string> = translatedRecord({
  COLOCATION: 'Coloc',
  MONTHLY: 'Au mois',
  NIGHTLY: 'À la nuit',
});

export const TYPE_LABEL: Record<string, string> = translatedRecord({
  ENTIRE_PLACE: 'Logement entier',
  PRIVATE_ROOM: 'Chambre privée',
  SHARED_ROOM: 'Chambre partagée',
});

export const BATHROOM_LABEL: Record<string, string> = translatedRecord({
  PRIVATE: 'Salle de bain privée',
  ENSUITE: 'Salle de bain dans la chambre',
  SHARED: 'Salle de bain partagée',
});

export const GENDER_LABEL: Record<string, string> = translatedRecord({
  ANY: 'Mixte',
  FEMALE_ONLY: 'Femmes uniquement',
  MALE_ONLY: 'Hommes uniquement',
});

export const STATUS_LABEL: Record<string, string> = translatedRecord({
  DRAFT: 'Brouillon',
  PENDING_REVIEW: 'En attente de visite',
  ACTIVE: 'En ligne',
  PAUSED: 'En pause',
  REMOVED: 'Retirée',
});

export const BOOKING_STATUS_LABEL: Record<string, string> = translatedRecord({
  PENDING: 'Demande envoyée',
  ACCEPTED: 'Acceptée — frais de service à régler',
  DECLINED: 'Refusée',
  AWAITING_PAYMENT: 'Paiement en cours',
  CONFIRMED: 'Confirmée',
  CANCELLED: 'Annulée',
  COMPLETED: 'Terminée',
  EXPIRED: 'Expirée',
});

export const BOOKING_STATUS_TONE: Record<string, string> = {
  PENDING: 'badge--warn',
  ACCEPTED: 'badge--warn',
  DECLINED: 'badge--danger',
  AWAITING_PAYMENT: 'badge--warn',
  CONFIRMED: 'badge--ok',
  CANCELLED: 'badge--danger',
  COMPLETED: 'badge--verified',
  EXPIRED: 'badge',
};

export const KYC_LABEL: Record<string, string> = translatedRecord({
  NONE: 'Non vérifiée',
  PENDING: 'En cours de vérification',
  APPROVED: 'Vérifiée',
  REJECTED: 'Refusée',
  EXPIRED: 'Expirée',
});

export const PAYMENT_LABEL: Record<string, string> = translatedRecord({
  CARD_CMI: 'Carte bancaire (CMI)',
  BANK_TRANSFER: 'Virement bancaire',
  CASH_AGENCY: 'Wafacash / Cash Plus',
  CASH_ON_ARRIVAL: 'Espèces à l’arrivée + acompte',
});

export const PAYMENT_HINT: Record<string, string> = translatedRecord({
  CARD_CMI: 'Visa, Mastercard — débit immédiat sécurisé',
  BANK_TRANSFER: 'RIB Bedly · sous 24 h ouvrées',
  CASH_AGENCY: 'Code de paiement en agence',
  CASH_ON_ARRIVAL: 'Acompte de 500 DH en ligne, le reste au check-in',
});

export const AMENITY_LABEL: Record<string, string> = translatedRecord({
  wifi: 'Wi-Fi',
  wifi_fiber: 'Wi-Fi fibre',
  heating: 'Chauffage',
  air_conditioning: 'Climatisation',
  kitchen: 'Cuisine',
  equipped_kitchen: 'Cuisine équipée',
  washing_machine: 'Machine à laver',
  parking: 'Parking',
  desk: 'Bureau d’étude',
  hot_water: 'Eau chaude',
  elevator: 'Ascenseur',
  balcony: 'Balcon',
  near_tram: 'Proche tram',
  near_bus: 'Proche bus',
  furnished_room: 'Chambre meublée',
  private_bathroom: 'Salle de bain privée',
  concierge: 'Concierge',
  security_door: 'Porte blindée',
});

export const ALL_AMENITIES = Object.keys(AMENITY_LABEL);

export const CITIES = [
  'Casablanca',
  'Rabat',
  'Marrakech',
  'Fès',
  'Tanger',
  'Agadir',
  'Ifrane',
  'Benguerir',
  'Meknès',
  'Oujda',
  'Kénitra',
  'Tétouan',
];

/** Deterministic tile gradient, so a city always looks the same. */
export function cityGradient(name: string): string {
  const palettes = [
    ['#B77445', '#8E5230'],
    ['#1F6F63', '#2E8577'],
    ['#C55A3C', '#9E4830'],
    ['#5B7A8C', '#3E5866'],
    ['#8C6D4A', '#6B5238'],
    ['#E8A33D', '#B07828'],
  ];
  let hash = 0;
  for (const character of name) hash = (hash * 31 + character.charCodeAt(0)) % 997;
  const palette = palettes[hash % palettes.length] as [string, string];
  return `linear-gradient(160deg, ${palette[0]}, ${palette[1]})`;
}

export const initials = (fullName: string): string =>
  fullName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

export const walkMinutes = (metres: number): number => Math.max(1, Math.round(metres / 80));
